import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Send } from "lucide-react";
import { PRICE_LABEL } from "@/lib/markets";
import VideoPlayer from "@/components/VideoPlayer";

const STATS = [
  ["Daily", "Bias & Key Levels"],
  ["Live", "Trade Sessions"],
  ["24/7", "Trader Desk"],
  [PRICE_LABEL.split(" ·")[0], "First Month"],
];

const Hero = () => {
  const navigate = useNavigate();

  return (
    <section
      className="relative flex min-h-screen items-center overflow-hidden pb-20 pt-36"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -right-40 -top-32 h-[34rem] w-[60rem] rotate-[-18deg] rounded-[100%] bg-[radial-gradient(ellipse_at_center,rgba(192,132,252,0.16),transparent_65%)] blur-3xl" />
        <div className="absolute -right-24 top-40 h-[26rem] w-[46rem] rotate-[-24deg] rounded-[100%] bg-[radial-gradient(ellipse_at_center,rgba(139,92,246,0.12),transparent_65%)] blur-3xl" />
        <div className="absolute -left-40 bottom-0 h-[22rem] w-[40rem] rotate-[14deg] rounded-[100%] bg-[radial-gradient(ellipse_at_center,rgba(76,29,149,0.08),transparent_65%)] blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)] lg:gap-14">
          <div className="min-w-0">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-slate-500"
          data-testid="hero-eyebrow"
        >
          (01) — Private Trading Community
        </motion.p>

        <h1
          className="mt-6 font-display text-5xl font-extrabold leading-[0.98] tracking-tight text-[#0a0a0a] sm:text-6xl lg:text-7xl"
          data-testid="hero-headline"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ delay: 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              Trade gold with
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              className="font-accent text-grad-aurora block"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              a real edge.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.8 }}
          className="mt-6 max-w-xl text-base text-slate-600 sm:text-lg"
          data-testid="hero-subheadline"
        >
          One private Telegram room. Daily XAU/USD bias, key levels and live trade
          sessions — with the reasoning behind every call.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-cta-button"
            onClick={() => navigate("/checkout")}
            className="btn-main group flex items-center gap-3 rounded-full px-8 py-4 font-sub text-base font-bold uppercase tracking-wide"
          >
            Join the club · {PRICE_LABEL.split(" ·")[0]}
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
          <button
            data-testid="hero-claim-link"
            onClick={() => navigate("/unlock")}
            className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#7c3aed] via-[#5b21b6] to-[#2e1065] px-5 py-3 font-mono2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white shadow-[0_10px_28px_rgba(76,29,149,0.4)] transition-all hover:shadow-[0_14px_38px_rgba(76,29,149,0.55)] hover:brightness-110"
          >
            <Send className="h-4 w-4" /> Already paid? Claim access
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="mt-4 font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400"
        >
          {PRICE_LABEL} · cancel anytime
        </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="min-w-0"
            data-testid="hero-video"
          >
            <VideoPlayer />
          </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 1 }}
          className="glass col-span-full mt-2 grid w-full grid-cols-2 overflow-hidden rounded-2xl shadow-[0_18px_48px_rgba(124,58,237,0.14)] sm:grid-cols-4"
          data-testid="hero-stats-grid"
        >
          {STATS.map(([v, l], index) => (
            <div
              key={l}
              className={`bg-white/90 px-4 py-6 ${index % 2 === 1 ? "border-l border-violet-500/60" : ""} ${index >= 2 ? "border-t border-violet-500/60 sm:border-t-0" : ""} ${index > 0 ? "sm:border-l sm:border-violet-500/60" : ""}`}
            >
              <p className="font-display text-xl font-bold text-[#0a0a0a]">{v}</p>
              <p className="mt-1 font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{l}</p>
            </div>
          ))}
        </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
